import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  IconButton,
  InputGroup,
  Select,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, InfoIcon, WarningIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { getCustomerProfilesApi, deleteCustomerApi } from 'api/adminApi';
import { getCustomerPipelineApi } from 'api/customerCockpitProfileApi';
import { getConnectorsApi } from 'api/connectorsApi';
import { buildCustomerStatusById } from 'pages/Cockpit/Pipeline/pipelineHelpers';
import DataTable from 'components/DataTable';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { DestructiveDialog } from 'components/Dialog';
import {
  CUSTOMER_STATUS_FILTER_OPTIONS,
  CUSTOMER_STATUS_UNSPECIFIED,
  DEFAULT_CUSTOMER_STATUS_FILTER,
  filterCustomers as applyCustomerFilters,
} from './customersOverviewHelpers';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddUserModal from './AddUserModal';

const columnHelper = createColumnHelper();

export const createColumns = (onDeleteClick, customerConnectors) => {
  const columns = [
    columnHelper.accessor('tenant', {
      cell: (info) => info.getValue() || 'Twelve',
      header: 'Tenant',
    }),
    columnHelper.accessor('companyName', {
      cell: (info) => info.getValue(),
      header: 'Company Name',
    }),
    columnHelper.accessor('pipelineStatus', {
      cell: (info) => info.getValue() || CUSTOMER_STATUS_UNSPECIFIED,
      header: 'Status',
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: 'Name',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) =>
        info.getValue() ? moment(info.getValue()).format('LLL') : ' - ',
      header: 'Created At',
    }),
    columnHelper.accessor('lastLoginAt', {
      cell: (info) =>
        info.getValue() ? moment(info.getValue()).format('LLL') : ' - ',
      header: 'Last Login',
    }),
    columnHelper.accessor('externalId', {
      cell: (info) => info.getValue(),
      header: 'External ID',
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => {
        const customerId = info.row.original.id;
        const hasConnectors = customerConnectors[customerId] > 0;

        return (
          <Tooltip
            label={
              hasConnectors
                ? "Disconnect all the customer's connectors before deleting the customer"
                : "This will hide the customer and their alerts from: Customers page, Performance pages, Alerts page, and Cash Flow Model. The customer will still appear in Asset Tape."
            }
            placement="top"
            hasArrow
          >
            <Flex align="center" gap={1}>
              <IconButton
                aria-label="Delete for Performance"
                icon={<DeleteIcon color={hasConnectors ? 'gray.400' : 'red'} />}
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                isDisabled={hasConnectors}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!hasConnectors) {
                    onDeleteClick(info.row.original);
                  }
                }}
              />
              {hasConnectors ? (
                <WarningIcon
                  color="orange.500"
                  boxSize={3}
                  cursor="help"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <InfoIcon
                  color="gray.500"
                  boxSize={3}
                  cursor="help"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </Flex>
          </Tooltip>
        );
      },
      header: 'Actions',
      enableSorting: false,
    }),
  ];

  return columns;
};

const defaultSorting = [{ id: 'createdAt', desc: true }];

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [tenantFilter, setTenantFilter] = useState('Treisar');
  const [statusFilter, setStatusFilter] = useState(DEFAULT_CUSTOMER_STATUS_FILTER);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [columns, setColumns] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [customerConnectors, setCustomerConnectors] = useState({});
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    setColumns(createColumns(onDeleteClick, customerConnectors));
  }, [customers, customerConnectors]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const [_customers, pipelineReport] = await Promise.all([
        getCustomerProfilesApi(),
        getCustomerPipelineApi(),
      ]);
      const statusByCustomerId = buildCustomerStatusById(pipelineReport);
      setCustomers(
        transformCustomers(_customers).map((customer) => ({
          ...customer,
          pipelineStatus:
            statusByCustomerId[customer.id] || CUSTOMER_STATUS_UNSPECIFIED,
        }))
      );
      setCompanyNames(_customers.map((c) => c.companyName));

      // Load connector counts for all customers
      const connectorCounts = {};
      await Promise.all(
        _customers.map(async (customer) => {
          try {
            const { connectors } = await getConnectorsApi(customer.id);
            connectorCounts[customer.id] = connectors ? connectors.length : 0;
          } catch (error) {
            // If we can't fetch connectors, assume they exist (safer default)
            connectorCounts[customer.id] = 1;
          }
        })
      );
      setCustomerConnectors(connectorCounts);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error loading customers',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const transformCustomers = (_customers) => {
    return _customers.map((customer) => {
      return {
        ...customer,
        tenant: customer.tenant || 'Twelve',
        createdAt: customer.createdAt ? new Date(customer.createdAt) : null,
        lastLoginAt: customer.lastLoginAt
          ? new Date(customer.lastLoginAt)
          : null,
      };
    });
  };

  const filterCustomers = (_customers) => {
    return applyCustomerFilters({
      customers: _customers,
      tenantFilter,
      statusFilter,
      searchValue,
    });
  };

  const onAddUserCellClick = (customerId) => {
    setSelectedCustomerId(customerId);
    setShowAddUserModal(true);
  };

  const navigateToConnectors = (customerId) => {
    navigate(`/customers/${customerId}/connectors`);
  };

  const onDeleteClick = (customer) => {
    // Button only shows when customer has no connectors, so we can proceed directly
    setDeleteError(null);
    setCustomerToDelete(customer);
  };

  const onDeleteCustomer = async () => {
    if (deleteError) return;

    try {
      await deleteCustomerApi(customerToDelete.id);
      toast({
        title: 'Success',
        description: 'Customer deleted for Performance successfully',
        status: 'success',
      });
      setCustomerToDelete(null);
      loadCustomers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete customer for Performance',
        status: 'error',
      });
    }
  };

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <Box maxW={'container.xl'}>
      <Card>
        <Flex justify={'space-between'}>
          <Flex gap={4} wrap="wrap" align="flex-start">
            <InputGroup mb={6} w={300}>
              {' '}
              <SearchBar
                h="44px"
                onChange={(event) => setSearchValue(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    const data = filterCustomers(customers);
                    if (data.length > 0) {
                      navigateToConnectors(data[0].id);
                    }
                  }
                }}
                w={{ lg: '390px' }}
                placeholder="Search by Company Name"
              />
            </InputGroup>
            <Select
              mb={6}
              w={160}
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            >
              <option value="Treisar">Treisar</option>
              <option value="Twelve">Twelve</option>
              <option value="All">All</option>
            </Select>
            <Box mb={6} w="200px">
              <SelectInput
                isMulti
                compactMulti
                compactMultiNoun="statuses"
                isClearable={false}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder="Status"
                options={CUSTOMER_STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(nextStatuses) =>
                  setStatusFilter(nextStatuses || [])
                }
              />
            </Box>
          </Flex>
          <Button
            variant="brand"
            minW="183px"
            fontSize="sm"
            fontWeight="500"
            ms="auto"
            onClick={() => {
              setSelectedCustomerId(null);
              setShowAddUserModal(true);
            }}
          >
            Create New Customer
          </Button>
        </Flex>

        <Box minH={300} mx={-5}>
          <DataTable
            data={filterCustomers(customers)}
            columns={columns}
            hasPagination={true}
            defaultSorting={defaultSorting}
            onRowClick={(row) => {
              navigateToConnectors(row.id);
            }}
            rowHeight={'100px'}
          />
        </Box>

        <AddUserModal
          show={showAddUserModal}
          header={'Create New Customer'}
          selectedCustomerId={selectedCustomerId}
          companyNames={companyNames}
          onSubmitSuccess={({ customerId }) => {
            navigate(`/customers/${customerId}/settings`);
          }}
          onModalClose={() => setShowAddUserModal(false)}
        />

        <DestructiveDialog
          header={deleteError ? 'Cannot Delete Customer for Performance' : 'Delete Customer for Performance'}
          body={
            deleteError
              ? deleteError
              : customerToDelete
              ? `Are you sure you want to delete ${customerToDelete.companyName} for Performance? This will hide the customer and their alerts from: Customers page, Performance pages, Alerts page, and Cash Flow Model. The customer will still appear in Asset Tape.`
              : null
          }
          deleteButtonText={deleteError ? 'OK' : 'Delete for Performance'}
          show={!!customerToDelete}
          onDelete={() => {
            if (deleteError) {
              setCustomerToDelete(null);
              setDeleteError(null);
            } else {
              onDeleteCustomer();
            }
          }}
          onDialogClose={() => {
            setCustomerToDelete(null);
            setDeleteError(null);
          }}
        />
      </Card>
    </Box>
  );
}

export default Customers;
